import type { GTMEcommerceReturnType, GTMEventReturnType, GTMItemsReturnType } from './GTM.types';
import { hashEmail } from '../util/encrypt';
import captureSafeException from '../util/sentry';

declare global {
    interface Window {
        dataLayer: (GTMEventReturnType | { ecommerce: null })[];
    }
}

enum MetaTagName {
    USER_HASH = 'user_hash',
    LOGIN_METHOD = 'login_method',
    USER_ID = 'user_id'
}

export default class GTM {
    public static clearEcommerce(): void {
        if (typeof window === 'undefined' || !window.dataLayer) {
            return;
        }

        window.dataLayer.push({ ecommerce: null });
    }

    public static track<DT extends GTMEventReturnType>(eventData: DT): void {
        if (typeof window === 'undefined' || !window.dataLayer) {
            return;
        }

        window.dataLayer.push(eventData);
    }

    public static trackEcommerce<DT extends GTMEventReturnType>(eventData: DT): void {
        try {
            if (typeof window === 'undefined' || !window.dataLayer) {
                return;
            }

            if ('ecommerce' in eventData) {
                GTM.clearEcommerce();
            }

            window.dataLayer.push(eventData);
        } catch (error) {
            const ecommerceData = eventData as Partial<
                GTMEcommerceReturnType<{ items?: Partial<GTMItemsReturnType>[] }>
            >;

            captureSafeException(error, 'failed_gtm_event', undefined, {
                parsing_context: 'trackEcommerce',
                region: 'GTM',
                eventName: ecommerceData?.ga4_event_name ?? 'unknown',
                houseId: ecommerceData?.ecommerce?.items?.[0]?.item_id ?? 'unknown'
            });
        }
    }

    public static async setMetaUserHash(email: string): Promise<void> {
        const emailHash = await hashEmail(email);
        this.#createMetaElement(MetaTagName.USER_HASH, emailHash);
    }

    public static setMetaLoginMethod(method: string): void {
        this.#createMetaElement(MetaTagName.LOGIN_METHOD, method);
    }

    public static setMetaUserId(userId: string): void {
        this.#createMetaElement(MetaTagName.USER_ID, userId);
    }

    static #createMetaElement(name: MetaTagName, content: string): void {
        let meta = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);

        if (!meta) {
            meta = document.createElement('meta');
            meta.name = name;
            document.head.appendChild(meta);
        }

        meta.setAttribute('content', content);
    }
}
